import { useState } from "react"
import { Button, Card, Dropdown, Flex, Tooltip } from "antd"
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { DeleteConfirmation } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/Deleteconfirmation"
import { Text } from "ui/Typography"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { useDevice } from "hooks/useDevice"
import styles from "./AdjustmentsSection.module.css"

const AdjustmentsSection = ({ title, tooltip, subtitle, items, onAdd, onEdit, onDelete, showAffects }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [deletingItem, setDeletingItem] = useState(null)

  const getDropdownItems = (item) => ({
    items: [
      {
        key: "delete",
        label: t("experiences.pricing.adjustment_modal.delete"),
        icon: <DeleteOutlined />,
        danger: true,
      },
    ],
    onClick: ({ key }) => {
      if (key === "delete") setDeletingItem(item)
    },
  })

  const renderActions = (item, className = "") => (
    <Flex align="center" className={`${styles.colActions} ${className}`}>
      <Button type="text" icon={<EditOutlined />} className={styles.actionBtn} onClick={() => onEdit(item)} />
      <Dropdown menu={getDropdownItems(item)} trigger={["click"]}>
        <Button type="text" icon={<MoreOutlined />} className={styles.actionBtn} />
      </Dropdown>
    </Flex>
  )

  // The table's columns are fixed-width and total more than a phone's viewport, so on mobile each
  // adjustment becomes a stacked card with the column headers repeated as field labels.
  const renderMobileList = () => (
    <Flex vertical gap={12}>
      {items.map((item) => (
        <div key={item.id} className={styles.listItem}>
          <Flex align="flex-start" gap={8}>
            <Text className={`${styles.rowName} ${styles.listItemName}`}>{item.name}</Text>
            {renderActions(item, styles.listItemActions)}
          </Flex>
          <Flex vertical gap={4} className={styles.listItemFields}>
            <Flex gap={8} justify="space-between">
              <Text className={styles.listItemLabel}>{t("experiences.pricing.adjustments_table.col_applies_on")}</Text>
              <Text className={styles.listItemValue}>{item.appliesOnDisplay}</Text>
            </Flex>
            <Flex gap={8} justify="space-between">
              <Text className={styles.listItemLabel}>{t("experiences.pricing.adjustments_table.col_base_rate")}</Text>
              <Text className={styles.listItemValue}>{item.rateChange}</Text>
            </Flex>
            {showAffects && (
              <Flex gap={8} justify="space-between">
                <Text className={styles.listItemLabel}>{t("experiences.pricing.adjustments_table.col_affects")}</Text>
                <Text className={styles.listItemValue}>{item.affectsDisplay}</Text>
              </Flex>
            )}
          </Flex>
        </div>
      ))}
    </Flex>
  )

  const renderTable = () => (
    <div className={styles.tableWrapper}>
      <Flex align="center" gap={12} className={styles.tableHeader}>
        <Text className={`${styles.colHeader} ${styles.colName}`}>{t("experiences.pricing.adjustments_table.col_name")}</Text>
        <Text className={`${styles.colHeader} ${styles.colApplies}`}>{t("experiences.pricing.adjustments_table.col_applies_on")}</Text>
        <Text className={`${styles.colHeader} ${styles.colRate}`}>{t("experiences.pricing.adjustments_table.col_base_rate")}</Text>
        {showAffects && <Text className={`${styles.colHeader} ${styles.colAffects}`}>{t("experiences.pricing.adjustments_table.col_affects")}</Text>}
        <div className={styles.colActions} />
      </Flex>

      {items.map((item) => (
        <Flex key={item.id} align="center" gap={12} className={styles.tableRow}>
          <Text className={`${styles.colName} ${styles.rowName}`}>{item.name}</Text>
          <Text className={`${styles.colApplies} ${styles.rowText}`}>{item.appliesOnDisplay}</Text>
          <Text className={`${styles.colRate} ${styles.rowText}`}>{item.rateChange}</Text>
          {showAffects && <Text className={`${styles.colAffects} ${styles.rowText}`}>{item.affectsDisplay}</Text>}
          {renderActions(item)}
        </Flex>
      ))}
    </div>
  )

  return (
    <Card>
    <Flex align="center" gap={16} className={styles.cardHeader}>
      <Flex align="center" gap={8} flex={1}>
        <Text className={styles.sectionTitle}>{title}</Text>
        <Tooltip title={tooltip}><InfoCircleOutlined className={styles.infoIcon} /></Tooltip>
      </Flex>
      <Button type="primary" shape="round" size="large" icon={<PlusOutlined />} onClick={onAdd}>
        {isMobile
          ? t("common.add")
          : t("experiences.pricing.adjustments_table.add_button")}
      </Button>
    </Flex>

    {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}

    <WithEmptyStateWrapper
      name="adjustment"
      tag="div"
      hasItems={items.length > 0}
      fallbackButtonText={t("experiences.pricing.adjustments_table.add_button")}
      fallbackButtonClickHandler={onAdd}
    >
      {isMobile ? renderMobileList() : renderTable()}
    </WithEmptyStateWrapper>

    <DeleteConfirmation
      visible={!!deletingItem}
      title={t("experiences.pricing.adjustment_modal.delete_confirm_title")}
      content={t("experiences.pricing.adjustment_modal.delete_confirm_content")}
      confirmText={t("experiences.pricing.adjustment_modal.delete")}
      cancelText={t("experiences.pricing.adjustment_modal.cancel")}
      onCancel={() => setDeletingItem(null)}
      onConfirm={() => {
        onDelete(deletingItem)
        setDeletingItem(null)
      }}
    />
    </Card>
  )
}

AdjustmentsSection.propTypes = {
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  subtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      appliesOnDisplay: PropTypes.string,
      rateChange: PropTypes.string.isRequired,
      affectsDisplay: PropTypes.string,
    })
  ).isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  showAffects: PropTypes.bool,
}

AdjustmentsSection.defaultProps = {
  tooltip: null,
  subtitle: null,
  showAffects: true,
}

export default AdjustmentsSection
